import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { motion, useInView } from 'motion/react'
import { cn } from '@/lib/utils'
import { EASE_OUT_EXPO } from '@/components/motion/reveal'
import { RollText } from '@/components/motion/roll-text'
import { ToneSection } from '@/components/generals/tone'

type Chapter = {
    year: string
    title: string
    text: string
    figure?: 'declines'
}

const CHAPTERS: Chapter[] = [
    {
        year: '2013',
        title: 'A gift card platform',
        text: 'Our story begins with a gift card platform we founded in 2013. After being hit with a very high chargeback rate on week one, we searched for a premium payment fraud protection solution that fits our type of product – and couldn’t find one.'
    },
    {
        year: '2013',
        title: 'Declining the wrong people',
        text: 'The market was filled with only payment fraud protection solutions that required us to decline 15%-20% of what we knew were legitimate shoppers who had gone through the entire purchase flow and provided us their payment information!',
        figure: 'declines'
    },
    {
        year: '2013',
        title: 'Built in-house',
        text: 'To meet our specific needs for prepaid & gift cards, we created our own in-house payment fraud prevention platform. It soon became clear that we weren’t the only merchant looking for such a solution.'
    },
    {
        year: '2019',
        title: 'nSure.ai is born',
        text: 'After consistently testing our machine learning models against leading payment fraud protection providers, in 2019 we saw an opportunity to make a greater difference in the crypto, gaming, and prepaid & gift card industries – and nSure.ai was born.'
    },
    {
        year: '2019',
        title: 'Spun out',
        text: 'Spun out of the giftcard platform, nSure.ai was co-founded by Alex Zeltcer and Ziv Isaiah, with the backing of Kamet, AXA’s venture builder at the time.'
    },
    {
        year: 'Today',
        title: 'Leadership',
        text: 'Alex leads the company as CEO; Ziv served as CTO from the founding until 2025.'
    }
]

const Declines: FC = (): ReactNode => (
    <div aria-hidden className='mt-8 flex flex-col gap-3'>
        <motion.div
            className='relative h-10 bg-tone/10'
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true, margin: '0px 0px -20% 0px' }}
        >
            <motion.span
                className='absolute inset-y-0 left-0 bg-ember'
                variants={{
                    hidden: { width: '0%' },
                    visible: { width: '20%', transition: { duration: 1.2, ease: EASE_OUT_EXPO } }
                }}
            />
            <span className='absolute inset-y-0 left-[15%] w-[5%] bg-[repeating-linear-gradient(135deg,transparent_0_4px,rgba(255,255,255,0.5)_4px_6px)]' />
        </motion.div>
        <div className='flex justify-between t-label text-tone-muted'>
            <span>
                15%-20% declined
            </span>
            <span>
                Legitimate shoppers
            </span>
        </div>
    </div>
)

const ChapterBlock: FC<{ chapter: Chapter, index: number, onActive: (index: number) => void }> = ({ chapter, index, onActive }): ReactNode => {
    const ref = useRef<HTMLLIElement>(null)
    const inView = useInView(ref, { margin: '-45% 0px -45% 0px' })

    useEffect(() => {
        if (inView) onActive(index)
    }, [inView, index, onActive])

    return (
        <li ref={ref} className='flex min-h-[55svh] flex-col justify-center border-t border-tone-line py-12'>
            <span className='t-label text-tone-muted'>
                {String(index + 1).padStart(2, '0')} · {chapter.year}
            </span>
            <h3 className={cn('mt-5 font-heading text-[clamp(2rem,3.6vw,3.25rem)] font-semibold uppercase leading-[0.95] tracking-[-0.03em] wdth-75 transition-opacity duration-700', inView ? 'opacity-100' : 'opacity-30')}>
                {chapter.title}
            </h3>
            <p className={cn('mt-5 max-w-xl text-[1.0625rem] leading-relaxed transition-colors duration-700', inView ? 'text-tone' : 'text-tone-muted')}>
                {chapter.text}
            </p>
            {chapter.figure === 'declines' && <Declines />}
        </li>
    )
}

// The founding story as a pinned year beside chapters that light up in turn.
export const Story: FC = (): ReactNode => {
    const [active, setActive] = useState(0)
    const year = CHAPTERS[active].year

    return (
        <ToneSection tone='paper' id='story' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
            <div className='grid gap-12 lg:grid-cols-12'>
                <div className='lg:col-span-5'>
                    <div className='flex flex-col gap-8 lg:sticky lg:top-28'>
                        <span className='t-label text-tone-muted'>
                            Our story
                        </span>
                        <RollText
                            value={year}
                            className='font-heading text-[clamp(6rem,15vw,15rem)] font-semibold leading-[0.8] tracking-[-0.06em] wdth-62'
                        />
                        <ol className='hidden flex-col gap-2 lg:flex' aria-hidden>
                            {CHAPTERS.map((chapter, index) => (
                                <li key={chapter.title} className={cn('flex items-center gap-3 t-label transition-colors duration-500', index === active ? 'text-tone' : 'text-tone-muted/60')}>
                                    <span className={cn('h-px transition-all duration-500', index === active ? 'w-10 bg-tone' : 'w-4 bg-tone-line')} />
                                    {chapter.title}
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>

                <ol className='lg:col-span-6 lg:col-start-7'>
                    {CHAPTERS.map((chapter, index) => (
                        <ChapterBlock key={chapter.title} chapter={chapter} index={index} onActive={setActive} />
                    ))}
                </ol>
            </div>
        </ToneSection>
    )
}
